import { apiJson } from "./api";

export const TRIAL_QUEUES = Object.freeze([
  { value: "today", label: "오늘 체험" },
  { value: "needs_record", label: "기록 필요" },
  { value: "follow_up", label: "오늘 연락" },
  { value: "overdue", label: "기한 지남" },
  { value: "all", label: "전체" },
]);

export const INQUIRY_SOURCES = Object.freeze([
  { value: "unknown", label: "미확인" },
  { value: "ad", label: "광고" },
  { value: "naver_search", label: "네이버 검색" },
  { value: "referral", label: "지인 추천" },
  { value: "walk_in", label: "지나가다 발견" },
  { value: "other", label: "기타" },
]);

export const BOOKING_SOURCES = Object.freeze([
  { value: "unknown", label: "미확인" },
  { value: "website", label: "홈페이지" },
  { value: "naver", label: "네이버" },
  { value: "phone", label: "전화" },
  { value: "walk_in", label: "현장" },
  { value: "staff", label: "직원 등록" },
  { value: "other", label: "기타" },
]);

export const GOALS = Object.freeze([
  { value: "fitness", label: "체력 향상" },
  { value: "weight_loss", label: "감량" },
  { value: "habit", label: "운동 습관" },
  { value: "performance", label: "운동 능력" },
  { value: "health", label: "건강 관리" },
  { value: "other", label: "기타" },
]);

export const INTENSITY_REACTIONS = Object.freeze([
  { value: "unknown", label: "미확인" },
  { value: "low", label: "낮았음" },
  { value: "appropriate", label: "적당했음" },
  { value: "high", label: "높았음" },
]);

export const EXPERIENCE_POINTS = Object.freeze([
  { value: "program", label: "프로그램" },
  { value: "coaching", label: "코칭" },
  { value: "facility", label: "시설" },
  { value: "atmosphere", label: "분위기" },
  { value: "changing_room", label: "탈의실" },
  { value: "restroom", label: "화장실" },
  { value: "accessibility", label: "접근성" },
  { value: "schedule", label: "시간표" },
  { value: "other", label: "기타" },
]);

export const OBJECTION_REASONS = Object.freeze([
  { value: "unknown", label: "미확인" },
  { value: "cost", label: "비용" },
  { value: "schedule", label: "일정" },
  { value: "distance", label: "거리" },
  { value: "intensity", label: "운동 강도" },
  { value: "effect_confidence", label: "효과 확신" },
  { value: "sustainability", label: "꾸준함" },
  { value: "facility", label: "시설" },
  { value: "other", label: "기타" },
]);

export const CONSULTATION_STATUSES = Object.freeze([
  { value: "not_started", label: "상담 전" },
  { value: "considering", label: "고민 중" },
  { value: "follow_up", label: "후속 연락" },
  { value: "on_hold", label: "보류" },
  { value: "closed", label: "종료" },
]);

export const CONTACT_CHANNELS = Object.freeze([
  { value: "in_person", label: "현장 상담" },
  { value: "phone", label: "전화" },
  { value: "sms", label: "문자" },
  { value: "naver", label: "네이버" },
  { value: "other", label: "기타" },
]);

export const CONTACT_RESULTS = Object.freeze([
  { value: "reached", label: "상담 완료" },
  { value: "no_answer", label: "부재" },
  { value: "message_sent", label: "메시지 발송" },
  { value: "follow_up_set", label: "재연락 예정" },
  { value: "closed", label: "상담 종료" },
]);

export const VISIT_LABELS = Object.freeze({
  unknown: "방문 미확인",
  booked: "예약",
  attended: "참석",
  cancelled: "취소",
});

// 방문 종류는 신청 행마다 붙는다(계약 visits[].kind). 한 에피소드에 체험과 드랍인이 섞인다
export const VISIT_KIND_LABELS = Object.freeze({
  trial: "체험",
  drop_in: "드랍인",
});

const LEGACY_STATUS_LABELS = Object.freeze({
  pending: "대기중",
  contacted: "연락완료",
  scheduled: "예약완료",
  converted: "참석완료",
  cancelled: "예약취소",
});

export function optionLabel(options, value, fallback = "미확인") {
  return options.find((option) => option.value === value)?.label || fallback;
}

export function legacyStatusLabel(value) {
  return LEGACY_STATUS_LABELS[value] || value || "미확인";
}

export function visitKindLabel(value) {
  return VISIT_KIND_LABELS[value] || VISIT_KIND_LABELS.trial;
}

export function visitStatusLabel(value) {
  return VISIT_LABELS[value] || VISIT_LABELS.unknown;
}

export function prettyPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value || "—";
}

// 운영 시간은 Asia/Seoul 고정이다(toDateTimeInput과 같은 기준). 브라우저 timezone에
// 기대면 KST가 아닌 기기에서 목록의 "다음 행동"과 같은 서랍의 datetime-local 입력칸이
// 같은 예정일을 다른 시각으로 보인다.
function seoulDateTime(value, options, fallback) {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    ...options,
    hour12: false,
  }).format(date);
}

export function shortDateTime(value) {
  return seoulDateTime(
    value,
    { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" },
    "—"
  );
}

// 수업일·최근 방문일처럼 서버가 KST 벽시계 날짜("2026-10-25")로 주는 자리에서 쓴다.
// 같은 seoulDateTime을 거치므로 KST가 아닌 기기에서도 날짜가 하루 밀리지 않는다.
export function shortDate(value) {
  return seoulDateTime(value, { month: "2-digit", day: "2-digit" }, "—");
}

// 회원 상세의 체험 이력처럼 연도까지 보이는 자리에서 쓰는 같은 KST 기준 포맷
export function fullDateTime(value) {
  return seoulDateTime(
    value,
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
    "날짜 미확인"
  );
}

export function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  // 운영 시간은 Asia/Seoul 고정(+09:00, DST 없음)이다. 브라우저/테스트 머신의
  // 로컬 timezone에 기대면 같은 예약이 실행 환경마다 다른 시각으로 보인다.
  return new Date(date.getTime() + 9 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 16);
}

export function fromDateTimeInput(value) {
  if (!value) return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const date = new Date(`${value}:00+09:00`);
  if (Number.isNaN(date.getTime()) || toDateTimeInput(date) !== value) {
    return null;
  }
  return date.toISOString();
}

// 서버는 빈 선택 문자열의 키를 아예 빼고 주므로(계약 "비어 있는 선택 문자열") 화면에서
// 매번 ?. 로 더듬지 않게 방문 한 줄의 칸을 모두 채워 둔다
function normalizeTrialVisit(visit = {}) {
  return {
    request_id: visit.request_id || "",
    kind: visit.kind === "drop_in" ? "drop_in" : "trial",
    status: visit.status || "unknown",
    booking_id: visit.booking_id || "",
    class_id: visit.class_id || "",
    class_date: visit.class_date || "",
    start_time: visit.start_time || "",
    class_title: visit.class_title || "",
    attended_at: visit.attended_at || null,
  };
}

// visits는 에피소드(같은 번호 + 31일 건 묶기) 안 모든 방문이고 신청 행 하나가 방문 한 번이다.
// visits가 없는 응답은 대표 행 방문 하나로 둔다. 에피소드에는 신청 행이 적어도 하나 있다.
function normalizeTrialVisits(customer, requestId, visitKind) {
  const visits = Array.isArray(customer.visits) ? customer.visits : [];
  if (visits.length) return visits.map(normalizeTrialVisit);
  return [
    normalizeTrialVisit({
      ...(customer.visit || {}),
      request_id: requestId,
      kind: visitKind,
    }),
  ];
}

// 요약은 서버가 세어 주고, 없으면 방문 목록에서 같은 규칙으로 센다(drop_in이 아니면 체험)
function normalizeVisitSummary(customer, visits) {
  const summary = customer.visit_summary || {};
  const dropIn = visits.filter((visit) => visit.kind === "drop_in").length;
  const dates = visits
    .map((visit) => visit.class_date)
    .filter(Boolean)
    .sort();
  return {
    trial_count:
      typeof summary.trial_count === "number"
        ? summary.trial_count
        : visits.length - dropIn,
    drop_in_count:
      typeof summary.drop_in_count === "number"
        ? summary.drop_in_count
        : dropIn,
    latest_visit_date:
      summary.latest_visit_date || dates[dates.length - 1] || "",
  };
}

// 목록 한 줄의 방문 요약("체험 1 · 드랍인 2 · 최근 10. 25."). 0인 종류는 아예 쓰지 않아
// 체험 한 번뿐인 흔한 줄이 "드랍인 0"으로 지저분해지지 않게 한다. 최근 방문일은 방문이
// 둘 이상일 때만 붙인다(한 번이면 옆 방문 일정 칸과 같은 날이다).
export function trialVisitSummaryText(summary = {}) {
  const trial = Number(summary.trial_count || 0);
  const dropIn = Number(summary.drop_in_count || 0);
  const parts = [];
  if (trial > 0) parts.push(`체험 ${trial}`);
  if (dropIn > 0) parts.push(`드랍인 ${dropIn}`);
  if (!parts.length) return "";
  if (trial + dropIn > 1 && summary.latest_visit_date) {
    parts.push(`최근 ${shortDate(summary.latest_visit_date)}`);
  }
  return parts.join(" · ");
}

// 방문 타임라인 한 줄의 때. 예약이 연결되지 않은 신청 행은 수업일이 없다
export function trialVisitWhen(visit = {}) {
  if (!visit.class_date) return "일정 미정";
  return [
    shortDate(visit.class_date),
    String(visit.start_time || "").slice(0, 5),
  ]
    .filter(Boolean)
    .join(" ");
}

// 목록 한 줄 = 에피소드 하나다. request_id/profile/visit/version은 대표 행(에피소드 안
// 가장 이른 체험 행, 체험이 없으면 가장 이른 행)의 값이고 visits는 에피소드 전체다
export function normalizeTrialSummary(customer = {}) {
  const requestId = customer.request_id || customer.id || "";
  const visitKind = customer.visit_kind || "trial";
  const visits = normalizeTrialVisits(customer, requestId, visitKind);
  return {
    request_id: requestId,
    name: customer.name || "",
    phone_number: customer.phone_number || "",
    legacy_status: customer.legacy_status || customer.status || "pending",
    visit_kind: visitKind,
    profile: customer.profile || {},
    visit: customer.visit || { status: "unknown" },
    visits,
    visit_summary: normalizeVisitSummary(customer, visits),
    registration: customer.registration || { registered: false },
    created_at: customer.created_at || "",
    updated_at: customer.updated_at || "",
    version: customer.version || customer.updated_at || "",
  };
}

// 상세도 대표 행 기준이다(비대표 id로 열어도 서버가 대표 행 상세를 준다). contacts는
// 에피소드 안 모든 신청 행의 기록을 최신순으로 합친 것이라 항목마다 request_id가 다를 수 있다
export function normalizeTrialDetail(detail = {}) {
  const request = detail.request || {};
  const summary = normalizeTrialSummary({
    request_id: request.id || detail.request_id,
    name: request.name || detail.name,
    phone_number: request.phone_number || detail.phone_number,
    legacy_status: request.legacy_status || detail.legacy_status,
    visit_kind: request.visit_kind || detail.visit_kind,
    profile: detail.profile,
    visit: detail.visit,
    visits: detail.visits,
    visit_summary: detail.visit_summary,
    registration: detail.registration,
    created_at: request.created_at || detail.created_at,
    updated_at: request.updated_at || detail.updated_at,
    version: detail.version,
  });
  return {
    ...summary,
    request: {
      id: summary.request_id,
      name: summary.name,
      phone_number: summary.phone_number,
      preferred_days: request.preferred_days || "",
      preferred_times: request.preferred_times || "",
      legacy_status: summary.legacy_status,
      visit_kind: summary.visit_kind,
      contact_note: request.contact_note || "",
      failure_reason: request.failure_reason || "",
      created_at: summary.created_at,
      updated_at: summary.updated_at,
    },
    contacts: Array.isArray(detail.contacts) ? detail.contacts : [],
    member_link: detail.member_link || { available: false, member_id: null },
  };
}

export function trialProfileDraft(customer = {}) {
  const profile = customer.profile || {};
  return {
    assigned_coach_id: profile.assigned_coach_id || "",
    inquiry_source: profile.inquiry_source || "unknown",
    booking_source: profile.booking_source || "unknown",
    goals: [...(profile.goals || [])],
    intensity_reaction: profile.intensity_reaction || "unknown",
    positive_points: [...(profile.positive_points || [])],
    friction_points: [...(profile.friction_points || [])],
    objection_reasons: [...(profile.objection_reasons || [])],
    customer_comment: profile.customer_comment || "",
    coach_note: profile.coach_note || "",
    next_action: profile.next_action || "",
    consultation_status: profile.consultation_status || "not_started",
    next_follow_up_at: toDateTimeInput(profile.next_follow_up_at),
    experience_recorded: !!profile.experience_recorded_at,
  };
}

// draft를 서버가 받는 값으로 바꾼다. 배열은 전체 교체, 빈 담당/예정일은 null이다
function trialUpdateFields(draft = {}) {
  return {
    assigned_coach_id: draft.assigned_coach_id || null,
    inquiry_source: draft.inquiry_source || "unknown",
    booking_source: draft.booking_source || "unknown",
    goals: [...(draft.goals || [])],
    intensity_reaction: draft.intensity_reaction || "unknown",
    positive_points: [...(draft.positive_points || [])],
    friction_points: [...(draft.friction_points || [])],
    objection_reasons: [...(draft.objection_reasons || [])],
    customer_comment: String(draft.customer_comment || "").trim(),
    coach_note: String(draft.coach_note || "").trim(),
    next_action: String(draft.next_action || "").trim(),
    consultation_status: draft.consultation_status || "not_started",
    next_follow_up_at: fromDateTimeInput(draft.next_follow_up_at),
    experience_recorded: !!draft.experience_recorded,
  };
}

// 저장은 부분 수정이다(API 계약 PUT 절): 키 생략은 변경 없음, 명시적 null은 비우기,
// 값은 설정이다. baseline은 상세를 열 때 만든 draft이며 그와 달라진 키만 보낸다.
// 전부 보내면 바꾸지 않은 체험 기록 시각이 지금으로 밀리고, 서버가 상담 종료 분기에서
// 같은 열을 다시 할당해 저장이 실패한다.
export function buildTrialUpdate(draft, version, baseline = null) {
  const next = trialUpdateFields(draft);
  const before = trialUpdateFields(baseline || {});
  const payload = { expected_version: version };
  Object.keys(next).forEach((key) => {
    const changed = Array.isArray(next[key])
      ? JSON.stringify(next[key]) !== JSON.stringify(before[key])
      : next[key] !== before[key];
    if (changed) payload[key] = next[key];
  });
  // 상담 종료는 서버가 다음 행동과 예정일을 함께 비운다. 같이 보내면 서버가 같은 열을
  // 두 번 할당하므로 종료로 바꿀 때는 두 키를 뺀다(연락 기록의 result=closed와 같다).
  if (payload.consultation_status === "closed") {
    delete payload.next_action;
    delete payload.next_follow_up_at;
  }
  return payload;
}

export function buildTrialContact(draft, version) {
  const payload = {
    expected_version: version,
    channel: draft.channel,
    result: draft.result,
    note: String(draft.note || "").trim(),
  };
  // Contact follow-up은 tri-state다: 빈 선택은 기존 계획 유지(키 생략), null은
  // 명시 clear, 값은 새 계획이다. closed는 서버가 상태와 계획을 함께 비운다.
  if (draft.result !== "closed") {
    if (draft.next_follow_up_at === null) {
      payload.next_follow_up_at = null;
    } else if (draft.next_follow_up_at) {
      const instant = fromDateTimeInput(draft.next_follow_up_at);
      if (instant) payload.next_follow_up_at = instant;
    }
  }
  return payload;
}

export function buildTrialCustomersQuery(filters = {}) {
  const params = new URLSearchParams();
  params.set("queue", filters.queue || "all");
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.assignee) params.set("assignee", filters.assignee);
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.bookingId) params.set("booking_id", filters.bookingId);
  params.set("limit", String(filters.limit || 20));
  params.set("offset", String(filters.offset || 0));
  return params.toString();
}

async function attachConflict(error) {
  if (error?.response?.status !== 409) throw error;
  try {
    const body = await error.response.json();
    error.currentVersion = body?.current_version || "";
    error.code = body?.error || "conflict";
  } catch {
    error.code = "conflict";
  }
  throw error;
}

export async function fetchTrialCustomers(filters) {
  const query = buildTrialCustomersQuery(filters);
  const response = await apiJson(`/v1/staff/trial-customers?${query}`);
  return {
    customers: Array.isArray(response?.customers)
      ? response.customers.map(normalizeTrialSummary)
      : [],
    total: Number(response?.total || 0),
    hasMore: !!response?.has_more,
  };
}

export async function fetchTrialCustomer(requestId) {
  const detail = await apiJson(
    `/v1/staff/trial-customers/${encodeURIComponent(requestId)}`
  );
  return normalizeTrialDetail(detail);
}

export async function fetchTrialCustomerByBooking(bookingId) {
  const result = await fetchTrialCustomers({
    queue: "all",
    bookingId,
    limit: 2,
    offset: 0,
  });
  if (result.customers.length !== 1) return null;
  return fetchTrialCustomer(result.customers[0].request_id);
}

export async function createTrialInquiry(payload) {
  const detail = await apiJson("/v1/staff/trial-customers", {
    method: "POST",
    body: JSON.stringify(payload),
    quietStatuses: [409],
  }).catch(attachConflict);
  return normalizeTrialDetail(detail);
}

export async function updateTrialCustomer(requestId, payload) {
  const detail = await apiJson(
    `/v1/staff/trial-customers/${encodeURIComponent(requestId)}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
      quietStatuses: [409],
    }
  ).catch(attachConflict);
  return normalizeTrialDetail(detail);
}

export async function appendTrialContact(requestId, payload) {
  return apiJson(
    `/v1/staff/trial-customers/${encodeURIComponent(requestId)}/contacts`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      quietStatuses: [409],
    }
  ).catch(attachConflict);
}

export async function fetchTrialCoaches() {
  const response = await apiJson("/v1/staff/trial-coaches");
  return Array.isArray(response?.coaches) ? response.coaches : [];
}

export async function updateLegacyTrialRequest(payload) {
  return apiJson("/v1/admin/free-trial-requests", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}
